import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {MsgBoxService} from '../../../../shared/msg-box.service';
import {I18NService} from '../../../../i18n/i18n.service';

export interface GseData extends Doc {
}

interface MimeType {
    name: string;
    type: string;
}

interface Doc {
    filename: string;
    file: File;
}

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const FILE_MIME_TYPES: MimeType[] = [
    {
        type: XLSX_MIME_TYPE,
        name: 'xlsx'
    }
];

const MAX_FILE_MB = 2;

@Component({
    selector: 'app-ec-gse-data',
    templateUrl: './ec-gse-data.component.html',
    styleUrls: ['./ec-gse-data.component.scss']
})
export class ECGseDataComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() date: Moment;
    @Output() values: EventEmitter<GseData> = new EventEmitter<GseData>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();

    month: string;
    year: number;
    uploadDocForm: FormGroup;
    currentDocument: Doc;
    MAX_FILE_MB = MAX_FILE_MB;
    readonly ACCEPTED_MIME_TYPES = FILE_MIME_TYPES.map(mt => mt.type).join(',');

    constructor(private fb: FormBuilder,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18NService: I18NService) {
        this.uploadDocForm = this.fb.group({
            filename: new FormControl(null, [Validators.required]),
            file: new FormControl(null, [Validators.required])
        });
    }

    get mimeTypesNames(): string {
        return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
    }

    ngOnInit() {
        this.month = moment(this.date.month() + 1, 'MM').format('MMMM');
        this.year = this.date.year();
        this.uploadDocForm.valueChanges.pipe(
            takeUntil(this.destroyed)
        ).subscribe(value => {
            this.values.emit({
                filename: value.filename,
                file: value.file
            });
        });
        this.uploadDocForm.statusChanges.pipe(
            takeUntil(this.destroyed)
        ).subscribe(() => {
            this.valid.emit(this.uploadDocForm.valid);
        });
    }

    uploadDocument(event: Event) {
        const files = 'files';
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                this.uploadDocForm.controls.filename.setValue(file.name);
                this.uploadDocForm.controls.file.setValue(file);
                this.currentDocument = {
                    filename: file.name,
                    file
                };
            }
        } else {
            this.uploadDocForm.controls.filename.setValue(null);
            this.uploadDocForm.controls.file.setValue(null);
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    removeDocument() {
        this.currentDocument = null;
        this.uploadDocForm.controls.filename.setValue(null);
        this.uploadDocForm.controls.file.setValue(null);
    }

    get mimeTypes(): string[] {
        return FILE_MIME_TYPES.map(mt => mt.type);
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18NService.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (!this.mimeTypes.includes(file.type)) {
            this.msgBoxService.error(this.i18NService.translate('Accepts only {0}', this.mimeTypesNames));
            return false;
        }
        return true;
    }
}


